// The admin console's icon set: inline SVG components on a 24px grid, stroked in
// currentColor at 1.5px (round caps/joins), rendered at 20px by default. Kept
// app-local on purpose — @recruiter/shared exposes no icon primitive (affordance
// glyphs there are inlined per component), and an icon package is a dependency
// this app doesn't need. All icons are decorative (aria-hidden); the accessible
// name always comes from adjacent text or the host control's aria-label.
import type { ReactNode, SVGProps } from "react";

import type { Area } from "../lib/areas";

export interface IconProps extends SVGProps<SVGSVGElement> {
  /** Rendered square size in px (viewBox stays 24). */
  size?: number;
}

function Icon({ size = 20, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function HomeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m3 10.5 9-7.5 9 7.5" />
      <path d="M5 9v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9" />
      <path d="M9.5 21v-6.5h5V21" />
    </Icon>
  );
}

/** Customer Support — a life buoy. */
export function LifeBuoyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
      <path d="m5.7 5.7 3.5 3.5" />
      <path d="m14.8 9.2 3.5-3.5" />
      <path d="m18.3 18.3-3.5-3.5" />
      <path d="m9.2 14.8-3.5 3.5" />
    </Icon>
  );
}

/** Talent Management — sparkles (curation, the recommended list). */
export function SparklesIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M11 4.5 12.6 8.9 17 10.5l-4.4 1.6L11 16.5 9.4 12.1 5 10.5l4.4-1.6z" />
      <path d="m18 14 .9 2.1L21 17l-2.1.9L18 20l-.9-2.1L15 17l2.1-.9z" />
    </Icon>
  );
}

/** User Administration — a shield with a check. */
export function ShieldIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3 5 5.8V11c0 4.6 2.9 8.4 7 10 4.1-1.6 7-5.4 7-10V5.8z" />
      <path d="m9.3 11.8 1.9 1.9 3.5-3.9" />
    </Icon>
  );
}

/** Permissions — a key. */
export function KeyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="7.5" cy="15.5" r="4" />
      <path d="m10.3 12.7 9.2-9.2" />
      <path d="m15.5 7.5 3 3" />
      <path d="m18.5 4.5 2 2" />
    </Icon>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m6 6 12 12" />
      <path d="m18 6-12 12" />
    </Icon>
  );
}

export function SignOutIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </Icon>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </Icon>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Icon>
  );
}

/** Empty states — an open inbox. */
export function InboxIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M22 12.5h-5.5l-2 3h-5l-2-3H2" />
      <path d="M5.5 5.5h13L22 12.5V17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4.5z" />
    </Icon>
  );
}

/** Error states — an alert circle. */
export function AlertIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4.5" />
      <path d="M12 16h.01" />
    </Icon>
  );
}

/** A compass — the Explorer: find your way to any user or project. */
export function CompassIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m15.5 8.5-2 5-5 2 2-5z" />
    </Icon>
  );
}

/** Developers — a bug under inspection, matching the area portrait. */
export function BugIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="8" y="7.5" width="8" height="12" rx="4" />
      <path d="M9.5 6a2.5 2.5 0 0 1 5 0" />
      <path d="M8 11.5H4.5" />
      <path d="M8 16H5" />
      <path d="M16 11.5h3.5" />
      <path d="M16 16h3" />
      <path d="M12 8v11.5" />
    </Icon>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 3v2" />
      <path d="M12 19v2" />
      <path d="m5.6 5.6 1.4 1.4" />
      <path d="m17 17 1.4 1.4" />
      <path d="M3 12h2" />
      <path d="M19 12h2" />
      <path d="m5.6 18.4 1.4-1.4" />
      <path d="m17 7 1.4-1.4" />
    </Icon>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 14.5A8 8 0 0 1 9.5 4 8 8 0 1 0 20 14.5z" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Icon>
  );
}

export function RocketIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 2.5c2.5 1.5 4.5 4.5 4.5 8 0 2-.7 3.7-1.6 5L12 18l-2.9-2.5c-.9-1.3-1.6-3-1.6-5 0-3.5 2-6.5 4.5-8Z" />
      <circle cx="12" cy="10" r="1.5" />
      <path d="M8.5 14.5 6 17c-.3 1-.3 2.5 0 3.5 1 .3 2.5.3 3.5 0l2.5-2.5" />
      <path d="M15.5 14.5 18 17c.3 1 .3 2.5 0 3.5-1 .3-2.5.3-3.5 0l-2.5-2.5" />
    </Icon>
  );
}

export function FunnelIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 5h16l-6 7v6.5l-4 2V12L4 5Z" />
    </Icon>
  );
}

export function EyeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  );
}

export function TagIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 4h7l9 9-7 7-9-9V4Z" />
      <circle cx="8" cy="8" r="1.4" />
    </Icon>
  );
}

/** Two bars either side of a dashed divider: the before/after comparison the
 *  Version Cohorts page is, with the deploy boundary between them. */
export function CohortIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 17V9M7 17v-4" />
      <path d="M12 3.5v17" strokeDasharray="3 3" />
      <path d="M17 17V6M21 17v-8" />
    </Icon>
  );
}

/** A path that forks into two: one population split into two arms, which is the
 *  whole of what an A/B test is. Deliberately NOT another bar chart — the two
 *  numbers pages either side of it in the sidebar already wear those. */
export function SplitIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 12h5" />
      <path d="M9 12c4 0 4-6 8-6h3" />
      <path d="M9 12c4 0 4 6 8 6h3" />
    </Icon>
  );
}

/** Three bars of rising height: the BI dashboard app this grant unlocks. */
export function BarChartIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 20V13M12 20V8M19 20V4" />
    </Icon>
  );
}

/** The nav/home/chip icon for each admin area (UI-only; keys are the contract). */
export const AREA_ICON: Record<Area, (props: IconProps) => ReactNode> = {
  explorer: CompassIcon,
  customer_support: LifeBuoyIcon,
  alerts: AlertIcon,
  talent_management: SparklesIcon,
  user_administration: ShieldIcon,
  developers: BugIcon,
  launch_day: RocketIcon,
  funnel: FunnelIcon,
  version_cohorts: CohortIcon,
  ab_tests: SplitIcon,
  // A key, not a shield: System Admin hands out access to the console rather
  // than guarding the product's users.
  system_admin: KeyIcon,
};
